import { motion } from 'framer-motion';
import { Target, Heart, Zap, Users } from 'lucide-react';

export default function About() {
  const leaders = [
    { id: 'founder', name: 'Coming Soon', role: 'Founder & Executive Director', image: 'https://via.placeholder.com/300' },
    { id: 'curriculum', name: 'Coming Soon', role: 'Head of Curriculum', image: 'https://via.placeholder.com/300' },
    { id: 'partnerships', name: 'Coming Soon', role: 'Director of Partnerships', image: 'https://via.placeholder.com/300' }
  ];

  return (
    <div className="bg-white" data-testid="about-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-unbounded font-black mb-8"
            data-testid="about-title"
          >
            About AI House USA
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-manrope max-w-4xl"
          >
            AI House USA is a residential training and builder program designed to accelerate real-world AI skill-building through structure, mentorship, and shipping.
          </motion.p>
        </div>
      </section>

      <section className="py-24" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-unbounded font-black mb-8">Our Mission</h2>
              <p className="text-lg font-manrope text-gray-700 mb-6">
                We believe the future belongs to builders who can harness AI. But most people learn in isolation, without structure, accountability, or community.
              </p>
              <p className="text-lg font-manrope text-gray-700">
                AI House USA solves this by creating a residential environment where builders live, learn, and ship together—transforming curiosity into real products in just 30 days.
              </p>
            </div>
            <div className="bg-gray-50 p-12 border-l-4 border-accent">
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <Target className="text-accent flex-shrink-0" size={32} />
                  <div>
                    <h3 className="font-unbounded font-bold text-xl mb-2">Focus</h3>
                    <p className="font-manrope text-gray-700">Distraction-free environment dedicated to building.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="text-accent flex-shrink-0" size={32} />
                  <div>
                    <h3 className="font-unbounded font-bold text-xl mb-2">Community</h3>
                    <p className="font-manrope text-gray-700">Live with like-minded builders who push you forward.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Zap className="text-accent flex-shrink-0" size={32} />
                  <div>
                    <h3 className="font-unbounded font-bold text-xl mb-2">Action</h3>
                    <p className="font-manrope text-gray-700">Ship real products, not just learn theory.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="why-now-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Why Now?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-black/10 p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">AI is Everywhere</h3>
              <p className="font-manrope text-gray-700">Every industry is being transformed. Builders who understand AI will shape the future.</p>
            </div>
            <div className="bg-white border border-black/10 p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Learning Gap</h3>
              <p className="font-manrope text-gray-700">Traditional education is too slow. Self-learning is too isolating. We bridge the gap.</p>
            </div>
            <div className="bg-white border border-black/10 p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Proven Model</h3>
              <p className="font-manrope text-gray-700">Residential accelerators work. We're applying this model to AI skill-building.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-16">Leadership</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {leaders.map((leader, index) => (
              <motion.div
                key={leader.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`leader-${leader.id}`}
              >
                <div className="bg-gray-200 aspect-square mb-6 flex items-center justify-center">
                  <Users size={64} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-unbounded font-bold mb-2">{leader.name}</h3>
                <p className="font-manrope text-gray-600">{leader.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
